import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";

import { useAdmin } from "../../../hooks/useAdmin";
import {
  GET_ESTABLISHMENTS_PAGE,
  buildEstablishmentsPageVariables,
  normalizeEstablishmentSearch,
} from "../graphQl/EstablishmentAPI";

// Délai après la dernière frappe avant d'interroger le serveur.
export const SEARCH_DEBOUNCE_MS = 300;

const normalizePage = (page) => Math.max(1, Number(page) || 1);

/**
 * Table de /admin/establishments : pagination, recherche et statut côté
 * serveur (une requête par page, propre à cette table). La liste centrale
 * d'AdminProvider n'est pas lue ici.
 *
 * - `search` : saisie brute. Le terme envoyé au serveur est appliqué après
 *   SEARCH_DEBOUNCE_MS, et seulement à partir de 3 caractères.
 * - `statusFilter` : "all" n'envoie pas de paramètre `status`.
 * - Un changement de terme appliqué ou de statut revient à la page 1.
 */
export const useManageEstablishment = () => {
  const { organization } = useAdmin();
  const organizationId = organization?.entityId;

  const [currentPage, setCurrentPageState] = useState(1);
  const [search, setSearchState] = useState("");
  const [appliedSearch, setAppliedSearch] = useState(undefined);
  const [statusFilter, setStatusFilterState] = useState("all");

  const setCurrentPage = useCallback((page) => {
    setCurrentPageState(normalizePage(page));
  }, []);

  const setSearch = useCallback((value) => {
    setSearchState(value);
  }, []);

  // Terme appliqué en dernier : la page ne repart à 1 que s'il change vraiment
  // (une frappe qui ne passe pas le seuil n'envoie rien et ne touche pas la page).
  const lastAppliedRef = useRef(undefined);
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = normalizeEstablishmentSearch(search);
      if (next === lastAppliedRef.current) return;
      lastAppliedRef.current = next;
      setAppliedSearch(next);
      setCurrentPageState(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const setStatusFilter = useCallback((value) => {
    setStatusFilterState(value);
    setCurrentPageState(1);
  }, []);

  const query = useQuery(GET_ESTABLISHMENTS_PAGE, {
    variables: buildEstablishmentsPageVariables({
      organizationId,
      currentPage: Math.max(0, currentPage - 1),
      search: appliedSearch,
      status: statusFilter === "all" ? undefined : statusFilter,
    }),
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  // Pendant le chargement d'un nouveau filtre ou d'une autre page, la page
  // précédente reste affichée (previousData) : la table ne disparaît pas.
  const freshPayload = query.data?.QR_LIST_ESTABLISHMENT;
  const freshPage = freshPayload?.data;
  const page = (freshPayload ?? query.previousData?.QR_LIST_ESTABLISHMENT)?.data;

  // Le controller peut répondre 200 avec l'erreur dans le corps (champ `error`),
  // en plus des erreurs GraphQL / réseau classiques. Une erreur sur la requête
  // en cours l'emporte sur la page précédente.
  const queryError = query.error;
  const payloadError = freshPayload?.error;
  const error = useMemo(() => {
    if (freshPage) return null;
    if (queryError) return queryError;
    return payloadError
      ? new Error(payloadError.message || "Establishment list error")
      : null;
  }, [freshPage, queryError, payloadError]);

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(
    () => (organizationId ? refetchQuery().catch(() => undefined) : Promise.resolve()),
    [organizationId, refetchQuery]
  );

  const nbPages = page?.totalPages || 0;

  // Suppression du dernier élément de la dernière page : elle n'existe plus,
  // on revient sur la nouvelle dernière page.
  useEffect(() => {
    if (nbPages > 0 && currentPage > nbPages) setCurrentPageState(nbPages);
  }, [nbPages, currentPage]);

  return {
    establishments: page?.content || [],
    // Premier chargement : aucune page à afficher encore.
    establishmentsLoading: query.loading && !page,
    // Nouveau filtre ou page : la page précédente reste affichée, grisée.
    establishmentsRefreshing: query.loading && Boolean(page),
    error,
    refetch,
    currentPage,
    setCurrentPage,
    nbPages,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
  };
};
